import { Route } from "vue-router";

import SnowplowSchemas from "@/assets/ts/SnowplowSchemas";
import { CookieService } from "@/services/CookieService";
import { Person } from "@/services/DTOs/Person";
import { ServicesSnowplowContext } from "@/services/DTOs/ServicesSnowplowContext";
import { UserSnowplowContext } from "@/services/DTOs/UserSnowplowContext";
import { IServiceDefinition } from "@/store/modules/services/Module";
import store from "@/store/store";
import { isTester } from "@/utils/TesterUtil";

import { ISnowplowContext, ISnowplowSchema, IStructuredEvent, SnowplowContext } from "./DTOs/SnowplowContext";

let currentUser: any = null;
let userContext: UserSnowplowContext | null = null;
let servicesContext: ServicesSnowplowContext | null = null;
let schemaSnowPlowList: any = null;

function signIn(person: Person, personServices?: number[]): void {
  const { subjectId, name, email, username, picture, id, hasBio, bioCompletion, locale, created } = person;
  getSnowPlow()("setUserId", subjectId);
  currentUser = { subjectId, name, email, username, picture, id, hasBio, bioCompletion, locale, created };
  currentUser.tester = isTester(currentUser);
  userContext = new UserSnowplowContext(person);

  if (personServices && personServices.length) {
    const servicesDefinition: IServiceDefinition[] | undefined = store.state.services.services;
    const attachDefinitionToPersonService = (personService: number): string => {
      const serviceDefinition = servicesDefinition ? servicesDefinition.find((s) => s.code === personService) : undefined;
      const serviceName = serviceDefinition ? serviceDefinition.name : "";

      return `:${personService}:${serviceName}`;
    };

    const parsedPersonServices = personServices.map(attachDefinitionToPersonService).join(",");

    servicesContext = new ServicesSnowplowContext({ personServices: parsedPersonServices });
  }
}

function updateUser(): void {
  const storeUser = store.state.users.authentication.user;
  const storeServices = store.state.users.authentication.services;

  if (!storeUser && currentUser) {
    currentUser = null;
    userContext = null;
    servicesContext = null;
  } else if (storeUser && !currentUser) {
    signIn(storeUser, storeServices);
  }
}

function loadSchemas() {
  return (SnowplowSchemas || {}) as { [key: string]: string };
}
​
function getSnowPlow() {
  // tslint:disable-next-line: no-empty
  return  (window as any).snowplow || (() => {
    // SnowPlow Mock this is useful when we run the app on a environment different to production
  });
}

function getFullName(to: Route): string {
  let fullPath = "Page:" + ( to.name || to.path );
  const viewer = " viewer:" + (currentUser ? "registered" : "anonymous");

  if (to.hash) {
    fullPath += " hash:" + to.hash;
  }
  fullPath += viewer;
  return fullPath;
}

function prepareEvent(eventName: string, payload?: object, optionalContext?: ISnowplowContext[]) {
  if (!schemaSnowPlowList) {
    schemaSnowPlowList = loadSchemas();
  }
  const schema = schemaSnowPlowList[eventName];
  if (!schema) {
    return null;
  }
  // This payload must be defined on each schema including user info as required false
  const payloadWithUserInfo = {
    ...currentUser,
    ...payload
  };

  const payloadClean = Object
  .keys(payloadWithUserInfo)
  .reduce((prev: any, cur) => {
    if (!!payloadWithUserInfo[cur]) {
      prev[cur] = payloadWithUserInfo[cur];
    }
    return prev;
  }, {});

  const emptyPersonServicesContext = new ServicesSnowplowContext({ personServices: "None" });
  const referrerContext = getReferrerContext();

  const contexts: ISnowplowSchema[] = referrerContext
  .concat(optionalContext || [])
  .concat(userContext || [])
  .concat(servicesContext || [emptyPersonServicesContext])
  .filter((context) => !!context && !!context.schemaName && !!schemaSnowPlowList[context.schemaName!])
  .map((parsedContext) => {
    const contextSchema = schemaSnowPlowList[parsedContext.schemaName!];
    return parsedContext.parseSchema(contextSchema);
  });

  return { event: { schema, data: payloadClean }, context: contexts };
}

function prepareStructuredEvent(payload: IStructuredEvent, optionalContext?: ISnowplowContext[]) {
  if (!schemaSnowPlowList) {
    schemaSnowPlowList = loadSchemas();
  }

  const emptyPersonServicesContext = new ServicesSnowplowContext({ personServices: "None" });
  const referrerContext = getReferrerContext();

  const contexts: ISnowplowSchema[] = referrerContext
  .concat(optionalContext || [])
  .concat(userContext || [])
  .concat(servicesContext || [emptyPersonServicesContext])
  .filter((context) => !!context)
  .map((parsedContext) => {
    if (parsedContext.schemaName) {
      const contextSchema = schemaSnowPlowList[parsedContext.schemaName];
      return parsedContext.parseSchema(contextSchema);
    } else {
      return parsedContext.parseSchema("");
    }
  });

  return {
    category: payload.category,
    action: payload.action,
    label: payload.label,
    property: payload.property,
    value: payload.value,
    context: contexts
  };
}

function page(to: Route, vue: any, from?: Route): void {
  updateUser();

  vue.$nextTick(() => {
    const event = "pageNavigation";
    const toPage = getFullName(to);
    const payload = {
      referrer: from ? from.fullPath : "",
      to: toPage
    };
    track(event, payload);
  });
}

function track(event: string, payload?: object, context?: ISnowplowContext[]): void {
  updateUser();
  const eventPayload = prepareEvent(event, payload, context);
  if (eventPayload) {
    getSnowPlow()("trackSelfDescribingEvent", eventPayload);
  }
}

function trackStructured(payload: IStructuredEvent, context?: ISnowplowContext[]): void {
  updateUser();

  const eventPayload = prepareStructuredEvent(payload, context);
  if (eventPayload) {
    getSnowPlow()("trackStructEvent", eventPayload);
  }
}

function getReferrerContext(): ISnowplowContext[] {
  const referralDeviceUuid = CookieService.getCookie("trackingId");
  const referrerDeviceUuid = CookieService.getCookie("referrerTrackingId");
  const referrerDestination = CookieService.getCookie("referrerDestination");
  const firstReferrerCode = CookieService.getCookie("referrerCode");
  const allReferrerCodes = CookieService.getCookie("referrerCodes");
  return [new SnowplowContext("ReferrerContext", {
    referralDeviceUuid,
    referrerDeviceUuid,
    referrerDestination,
    firstReferrerCode,
    allReferrerCodes
  })];
}

export function trackAPICall(action: string, service: string, item: string, mapper?: (args: IArguments) => any) {
  return (target: any, propertyKey: string, descriptor: any) => {
    const originalMethod = descriptor.value;
    descriptor.value = function() {
      const name = `APICall:${action}:${service}:${item}`;
      let payload: any = {
        event: name,
        action,
        service,
        item
      };

      if (mapper) {
        payload = { ...payload, ...mapper(arguments) };
      }

      track(name, payload);
      return originalMethod.apply(this, arguments);
    };
    return descriptor;
  };
}

export const SnowplowService = {
  page,
  track,
  trackStructured
};
